import axios from "axios";
import constants from "configs/constants";

// API Base URL - can be moved to env if needed
const PINK_SALE_API_BASE_URL =
  process.env.REACT_APP_PINK_SALE_API_URL || "http://localhost:3000";

// Types
export interface CriterionParam {
  amountUsd?: number;
  [key: string]: any;
}

export interface Criterion {
  type: string;
  enabled: boolean;
  params: CriterionParam;
}

export interface PreviewEligibleRequest {
  weekNumber: number;
  year: number;
  timestamp: string;
  criteria: Criterion[];
  isPreviewAllProjects: boolean;
}

export interface EvaluationDetail {
  required: number;
  actual: number;
  passed: boolean;
}

export interface EvaluationDetails {
  [key: string]: EvaluationDetail;
}

export interface Project {
  launchpadId: string;
  chainId: number;
  poolAddress: string;
  tokenName: string;
  tokenSymbol: string;
  totalRaisedUSD: number;
  finishTime: string;
  evaluationDetails: EvaluationDetails;
}

export interface EligibilityPeriod {
  startTime: string;
  endTime: string;
}

export interface PreviewEligibleResponse {
  success: boolean;
  eligibilityPeriod: EligibilityPeriod;
  criteria: Criterion[];
  totalEligible: number;
  projects: Project[];
}

// Helper function to get access token
const getAccessToken = (): string | null => {
  return localStorage.getItem(constants.STORAGE.PINK_SALE_ACCESS_TOKEN);
};

// Create axios instance for PinkSale API
const createPinkSaleInstance = () => {
  const instance = axios.create({
    baseURL: PINK_SALE_API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Accept: "*/*",
    },
  });

  // Add request interceptor to include Bearer token
  instance.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return instance;
};

const pinkSaleInstance = createPinkSaleInstance();

/**
 * Preview eligible projects for voting period
 * @param payload - Request payload with week number, year, timestamp, criteria, and preview flag
 * @returns Promise with eligible projects data
 */
export const previewEligible = async (
  payload: PreviewEligibleRequest
): Promise<PreviewEligibleResponse> => {
  try {
    const { data } = await pinkSaleInstance.post<PreviewEligibleResponse>(
      "/api/v1/voting-period/admin/preview-eligible",
      payload
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to preview eligible projects"
      );
    }
    throw error;
  }
};

// Voting Period Types
export interface VotingPeriodStats {
  totalVotes: number;
  totalVotingPower: number;
  uniqueVotersCount: number;
  totalEligibleProjects: number;
}

export interface VotingPeriod {
  _id: string;
  startTime: string;
  endTime: string;
  isPaused: boolean;
  isCompleted: boolean;
  weekNumber: number;
  year: number;
  eligibilityStartTime: string;
  eligibilityEndTime: string;
  eligibilityCriteria: Criterion[];
  winnerId: string | null;
  pausedAt: string | null;
  pausedBy: string | null;
  createdAt: string;
  updatedAt: string;
  stats?: VotingPeriodStats;
}

export interface GetVotingPeriodsParams {
  page?: number;
  limit?: number;
}

export interface VotingPeriodsResponse {
  docs: VotingPeriod[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
}

/**
 * Get voting periods with pagination
 * @param params - Query parameters with page and limit
 * @returns Promise with paginated voting periods data
 */
export const getVotingPeriods = async (
  params: GetVotingPeriodsParams = {}
): Promise<VotingPeriodsResponse> => {
  try {
    const { page = 1, limit = 20 } = params;
    const { data } = await pinkSaleInstance.get<VotingPeriodsResponse>(
      "/api/v1/voting-period",
      {
        params: {
          page,
          limit,
        },
      }
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch voting periods"
      );
    }
    throw error;
  }
};

/**
 * Get voting period details by ID
 * @param votingPeriodId - The ID of the voting period
 * @returns Promise with voting period details
 */
export const getVotingPeriodById = async (
  votingPeriodId: string
): Promise<VotingPeriod> => {
  try {
    const { data } = await pinkSaleInstance.get<VotingPeriod>(
      `/api/v1/voting-period/${votingPeriodId}`
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch voting period details"
      );
    }
    throw error;
  }
};

// Project Types
export interface Token {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  _id: string;
}

export interface Currency {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  _id: string;
}

export interface Pool {
  address: string;
  version: number;
  router: string;
  owner: string;
  currency: string;
  state: number;
  rate: string;
  listingRate: string;
  minRate: string | null;
  maxRate: string | null;
  cycle: string | null;
  softCap: string;
  hardCap: string | null;
  softCapTokens: string | null;
  totalSellingTokens: string | null;
  hardCapTokensPerUser: string | null;
  totalRaised: string;
  totalCommitted: string | null;
  min: string | null;
  max: string | null;
  startTime: number;
  endTime: number;
  publicSaleStartTime: number;
  finishTime: number;
  claimTime: string | null;
  liquidityPercentage: number;
  buybackPercentage: number | null;
  liquidityLockDuration: number;
  poolDetails: string;
  kycDetails: string;
  hasKyc: boolean;
  hasAudit: boolean;
  hasSafu: boolean;
  affiliateBps: number;
  totalVolumePurchased: string | null;
  formattedRate: number;
  formattedListingRate: number;
  formattedSoftCap: number;
  formattedHardCap: number | null;
  formattedTotalRaised: number;
  formattedTotalCommitted: number | null;
  formattedMin: number | null;
  formattedMax: number;
  formattedMinRate: number | null;
  formattedMaxRate: number | null;
  formattedSoftCapTokens: number | null;
  formattedTotalSellingTokens: number | null;
  subscriptionStage: string | null;
  whitelistApprobationLink: string | null;
  whitelistManager: string;
  locker: string | null;
  contributorCount: number | null;
  refundType: number | null;
  subscriptionType: string | null;
  tonDetails: any | null;
  suiDetails: any | null;
  solanaDetails: any | null;
  _id: string;
}

export interface PriceData {
  base: number;
  usd: number;
}

export interface LaunchpadMetadata {
  finalizeTotalRaisedUsd?: number;
  finalizeCurrencyPriceUsd?: number;
  ath?: PriceData;
  atl?: PriceData;
  current?: PriceData;
  listingPrice?: PriceData;
  presalePrice?: PriceData;
}

export interface Launchpad {
  _id: string;
  chainId: number;
  index: number;
  poolAddress: string;
  token: Token;
  currency: Currency;
  pool: Pool;
  query: string;
  initialMarketCap: number | null;
  tonRouterAddress: string | null;
  createdAt: string;
  updatedAt: string;
  metadata?: LaunchpadMetadata;
}

export interface VotingPeriodProject {
  _id: string;
  campaignId: string;
  launchpadId: string;
  chainId: number;
  poolAddress: string;
  status: string;
  votingPower: number;
  addedBy: string | null;
  createdAt: string;
  updatedAt: string;
  launchpad: Launchpad;
}

/**
 * Get projects for a voting period
 * @param votingPeriodId - The ID of the voting period
 * @returns Promise with array of projects
 */
export const getVotingPeriodProjects = async (
  votingPeriodId: string
): Promise<VotingPeriodProject[]> => {
  try {
    const { data } = await pinkSaleInstance.get<VotingPeriodProject[]>(
      `/api/v1/voting-period/${votingPeriodId}/projects`
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch voting period projects"
      );
    }
    throw error;
  }
};

// Vote Types
export interface Vote {
  _id: string;
  campaignId: string;
  voterAddress: string;
  projectId: string;
  chainId: number;
  votingPower: number;
  createdAt: string;
}

/**
 * Get votes for a voting period
 * @param votingPeriodId - The ID of the voting period
 * @returns Promise with array of votes
 */
export const getVotingPeriodVotes = async (
  votingPeriodId: string
): Promise<Vote[]> => {
  try {
    const { data } = await pinkSaleInstance.get<Vote[]>(
      `/api/v1/voting-period/${votingPeriodId}/votes`
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch voting period votes"
      );
    }
    throw error;
  }
};

// Project Votes Response Types
export interface ProjectVotesResponse {
  votes: Vote[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GetProjectVotesParams {
  page?: number;
  limit?: number;
}

/**
 * Get votes for a specific project in a voting period with pagination
 * @param votingPeriodId - The ID of the voting period
 * @param projectId - The ID of the project
 * @param params - Query parameters with page and limit
 * @returns Promise with paginated votes data
 */
export const getProjectVotes = async (
  votingPeriodId: string,
  projectId: string,
  params: GetProjectVotesParams = {}
): Promise<ProjectVotesResponse> => {
  try {
    const { page = 1, limit = 20 } = params;
    const { data } = await pinkSaleInstance.get<ProjectVotesResponse>(
      `/api/v1/voting-period/${votingPeriodId}/projects/${projectId}/votes`,
      {
        params: {
          page,
          limit,
        },
      }
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch project votes"
      );
    }
    throw error;
  }
};

// Admin API Types
export interface CreateVotingPeriodRequest {
  // ISO date string, required
  time: string;
  eligibilityCriteria?: Criterion[]; // Optional
}

export interface UpdateCampaignTimesRequest {
  startTime?: string; // ISO date string, optional
  endTime?: string; // ISO date string, optional
}

export interface UpdateCampaignCriteriaRequest {
  eligibilityCriteria: Criterion[];
}

export interface CampaignResponse {
  success: boolean;
  campaign: VotingPeriod;
}

export interface SetWinnerRequest {
  launchpadId: string; // MongoDB ObjectId
}

export interface EligibleProject {
  _id: string;
  campaignId: string;
  launchpadId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface AddEligibleProjectRequest {
  launchpadId: string; // MongoDB ObjectId
}

export interface AddEligibleProjectResponse {
  success: boolean;
  project: EligibleProject;
}

export interface RemoveEligibleProjectResponse {
  success: boolean;
  project: EligibleProject;
}

export interface CalculateEligibleResponse {
  success: boolean;
  eligibleProjectsCount: number;
}

/**
 * Create a new voting period
 * @param payload - Request payload with startTime, endTime, weekNumber, year, eligibilityStartTime, eligibilityEndTime, and optional eligibilityCriteria
 * @returns Promise with created campaign data
 */
export const createVotingPeriod = async (
  payload: CreateVotingPeriodRequest
): Promise<CampaignResponse> => {
  try {
    const { data } = await pinkSaleInstance.post<CampaignResponse>(
      "/api/v1/voting-period/admin",
      payload
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to create voting period"
      );
    }
    throw error;
  }
};

/**
 * Update campaign times (startTime and/or endTime)
 * @param campaignId - The ID of the campaign
 * @param payload - Request payload with optional startTime and endTime
 * @returns Promise with updated campaign data
 */
export const updateCampaignTimes = async (
  campaignId: string,
  payload: UpdateCampaignTimesRequest
): Promise<CampaignResponse> => {
  try {
    const { data } = await pinkSaleInstance.patch<CampaignResponse>(
      `/api/v1/voting-period/admin/${campaignId}/times`,
      payload
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update campaign times"
      );
    }
    throw error;
  }
};

/**
 * Update campaign eligibility criteria
 * @param campaignId - The ID of the campaign
 * @param payload - Request payload with eligibilityCriteria
 * @returns Promise with updated campaign data
 */
export const updateCampaignCriteria = async (
  campaignId: string,
  payload: UpdateCampaignCriteriaRequest
): Promise<CampaignResponse> => {
  try {
    const { data } = await pinkSaleInstance.patch<CampaignResponse>(
      `/api/v1/voting-period/admin/${campaignId}/criteria`,
      payload
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update campaign criteria"
      );
    }
    throw error;
  }
};

/**
 * Pause a campaign
 * @param campaignId - The ID of the campaign
 * @returns Promise with updated campaign data
 */
export const pauseCampaign = async (
  campaignId: string
): Promise<CampaignResponse> => {
  try {
    const { data } = await pinkSaleInstance.post<CampaignResponse>(
      `/api/v1/voting-period/admin/${campaignId}/pause`
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to pause campaign"
      );
    }
    throw error;
  }
};

/**
 * Resume a paused campaign
 * @param campaignId - The ID of the campaign
 * @returns Promise with updated campaign data
 */
export const resumeCampaign = async (
  campaignId: string
): Promise<CampaignResponse> => {
  try {
    const { data } = await pinkSaleInstance.post<CampaignResponse>(
      `/api/v1/voting-period/admin/${campaignId}/resume`
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to resume campaign"
      );
    }
    throw error;
  }
};

/**
 * Set winner for a campaign
 * @param campaignId - The ID of the campaign
 * @param payload - Request payload with launchpadId
 * @returns Promise with updated campaign data
 */
export const setWinner = async (
  campaignId: string,
  payload: SetWinnerRequest
): Promise<CampaignResponse> => {
  try {
    const { data } = await pinkSaleInstance.post<CampaignResponse>(
      `/api/v1/voting-period/admin/${campaignId}/set-winner`,
      payload
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message || error.message || "Failed to set winner"
      );
    }
    throw error;
  }
};

/**
 * Add an eligible project to a campaign
 * @param campaignId - The ID of the campaign
 * @param payload - Request payload with launchpadId
 * @returns Promise with added project data
 */
export const addEligibleProject = async (
  campaignId: string,
  payload: AddEligibleProjectRequest
): Promise<AddEligibleProjectResponse> => {
  try {
    const { data } = await pinkSaleInstance.post<AddEligibleProjectResponse>(
      `/api/v1/voting-period/admin/${campaignId}/eligible-projects`,
      payload
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to add eligible project"
      );
    }
    throw error;
  }
};

/**
 * Remove an eligible project from a campaign
 * @param campaignId - The ID of the campaign
 * @param projectId - The ID of the project to remove
 * @returns Promise with removed project data
 */
export const removeEligibleProject = async (
  campaignId: string,
  projectId: string
): Promise<RemoveEligibleProjectResponse> => {
  try {
    const { data } =
      await pinkSaleInstance.delete<RemoveEligibleProjectResponse>(
        `/api/v1/voting-period/admin/${campaignId}/eligible-projects/${projectId}`
      );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to remove eligible project"
      );
    }
    throw error;
  }
};

/**
 * Calculate eligible projects for a campaign
 * @param campaignId - The ID of the campaign
 * @returns Promise with eligible projects count
 */
export const calculateEligibleProjects = async (
  campaignId: string
): Promise<CalculateEligibleResponse> => {
  try {
    const { data } = await pinkSaleInstance.post<CalculateEligibleResponse>(
      `/api/v1/voting-period/admin/${campaignId}/calculate-eligible`
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Failed to calculate eligible projects"
      );
    }
    throw error;
  }
};
